import React, { useEffect, useState } from "react";
import { Box, Button, TextField, Typography, Alert, CircularProgress } from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import SmsIcon from "@mui/icons-material/Sms";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";

// Activation step for a new signup: the person picks an emailed link or an SMS code, and must
// finish it before the first sign-in. `activation` is what /api/signup or a refused /api/login
// returned (activationTicket, activationMethods, emailHint, phoneHint). onActivated runs after a
// right SMS code, or when the person says they opened the email link; callers then sign in and
// return false when the server says the account is still not activated.

async function post(path, body) {
  const res = await fetch(`/api/auth/activation/${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) throw new Error(data.message || "Something went wrong. Please try again.");
  return data;
}

const choiceSx = (selected) => ({
  display: "flex", alignItems: "center", gap: 1.5, width: "100%", textAlign: "left",
  p: 1.5, mb: 1.25, borderRadius: "10px", cursor: "pointer", bgcolor: selected ? "#eef2ff" : "#fff",
  border: `2px solid ${selected ? "#4f46e5" : "#e5e7eb"}`, font: "inherit", color: "#111827",
  "&:hover": { borderColor: "#6366f1" },
});

export default function AccountActivation({ activation, onActivated, onBack }) {
  const methods = activation?.activationMethods || [];
  const ticket = activation?.activationTicket;
  const [method, setMethod] = useState(methods[0] || "");
  const [sent, setSent] = useState(null); // { method, sentTo }
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [wait, setWait] = useState(0);

  useEffect(() => {
    if (wait <= 0) return undefined;
    const t = setTimeout(() => setWait((w) => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const send = async (m = method) => {
    setBusy(true);
    setError("");
    try {
      const d = await post("send", { ticket, method: m });
      setSent({ method: d.method, sentTo: d.sentTo });
      setMethod(d.method);
      setCode("");
      setWait(d.resendInSeconds || 60);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (!/^\d{6}$/.test(code.trim())) {
      setError("Enter the 6-digit code from the SMS.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await post("verify-otp", { ticket, code: code.trim() });
      onActivated?.();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const checkEmailDone = async () => {
    setBusy(true);
    setError("");
    const ok = await onActivated?.();
    if (ok === false) setError("Your account is not activated yet. Open the link in the email first.");
    setBusy(false);
  };

  const other = methods.find((m) => m !== sent?.method);
  const label = (m) => (m === "EMAIL" ? `email to ${activation.emailHint}` : `SMS to ${activation.phoneHint}`);

  if (!methods.length) {
    return <Alert severity="info">Your account is ready. Please sign in.</Alert>;
  }

  return (
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#111827" }}>Activate your account</Typography>
      <Typography sx={{ color: "#6b7280", fontSize: "0.88rem", mt: 0.5, mb: 2 }}>
        {sent ? "Almost done." : "Before you sign in for the first time, confirm it is you. How should we send it?"}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "8px", fontSize: "0.82rem" }}>{error}</Alert>}

      {!sent && (
        <>
          {methods.includes("EMAIL") && (
            <Box component="button" type="button" onClick={() => setMethod("EMAIL")} sx={choiceSx(method === "EMAIL")}>
              <EmailIcon sx={{ color: "#4f46e5" }} />
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: "0.92rem" }}>Email me an activation link</Typography>
                <Typography sx={{ color: "#6b7280", fontSize: "0.8rem" }}>{activation.emailHint}</Typography>
              </Box>
            </Box>
          )}
          {methods.includes("SMS") && (
            <Box component="button" type="button" onClick={() => setMethod("SMS")} sx={choiceSx(method === "SMS")}>
              <SmsIcon sx={{ color: "#4f46e5" }} />
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: "0.92rem" }}>Text me a code (OTP)</Typography>
                <Typography sx={{ color: "#6b7280", fontSize: "0.8rem" }}>{activation.phoneHint}</Typography>
              </Box>
            </Box>
          )}
          <Button fullWidth variant="contained" disabled={busy || !method} onClick={() => send()}
            sx={{ mt: 1, py: 1.1, borderRadius: "10px", textTransform: "none", fontWeight: 700 }}>
            {busy ? <CircularProgress size={20} color="inherit" /> : method === "EMAIL" ? "Send activation email" : "Send code"}
          </Button>
        </>
      )}

      {sent?.method === "SMS" && (
        <form onSubmit={verify}>
          <Typography sx={{ fontSize: "0.88rem", color: "#374151", mb: 1.5 }}>
            We sent a 6-digit code to <b>{sent.sentTo}</b>. It works for 10 minutes.
          </Typography>
          <TextField
            autoFocus fullWidth label="Code" value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputProps={{ inputMode: "numeric", autoComplete: "one-time-code", style: { letterSpacing: 6, fontSize: 20 } }}
          />
          <Button type="submit" fullWidth variant="contained" disabled={busy || code.length !== 6}
            sx={{ mt: 2, py: 1.1, borderRadius: "10px", textTransform: "none", fontWeight: 700 }}>
            {busy ? <CircularProgress size={20} color="inherit" /> : "Activate and sign in"}
          </Button>
        </form>
      )}

      {sent?.method === "EMAIL" && (
        <Box sx={{ textAlign: "center" }}>
          <MarkEmailReadIcon sx={{ fontSize: 44, color: "#16a34a", mb: 1 }} />
          <Typography sx={{ fontSize: "0.9rem", color: "#374151", mb: 2 }}>
            We sent an activation link to <b>{sent.sentTo}</b>. Open it to activate your account, then come back and sign in.
            Check your spam folder if you don't see it.
          </Typography>
          <Button fullWidth variant="contained" disabled={busy} onClick={checkEmailDone}
            sx={{ py: 1.1, borderRadius: "10px", textTransform: "none", fontWeight: 700 }}>
            I've opened the link, sign me in
          </Button>
        </Box>
      )}

      {sent && (
        <Box sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mt: 1.5 }}>
          <Button size="small" disabled={busy || wait > 0} onClick={() => send(sent.method)} sx={{ textTransform: "none" }}>
            {wait > 0 ? `Send again in ${wait}s` : sent.method === "EMAIL" ? "Send the email again" : "Send a new code"}
          </Button>
          {other && (
            <Button size="small" disabled={busy || wait > 0} onClick={() => { setSent(null); setMethod(other); }} sx={{ textTransform: "none" }}>
              Use {label(other)} instead
            </Button>
          )}
        </Box>
      )}

      {onBack && (
        <Button size="small" onClick={onBack} sx={{ mt: 1, textTransform: "none", color: "#6b7280" }}>Back to sign in</Button>
      )}
    </Box>
  );
}
